import { Head } from "vite-react-ssg";
import { SITE_URL } from "./env";

export interface SeoMeta {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  noIndex?: boolean;
  /** Optional JSON-LD graph rendered into <head> as application/ld+json. */
  jsonLd?: unknown;
}

// Escape "<" so a "</script>" inside stringified JSON-LD can't break out of the
// <script> element (stored-XSS guard; mirrors the blog engine's jsonLdSafe).
function jsonLdSafe(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/**
 * Per-route document head. Renders through vite-react-ssg's <Head>, so the
 * title/meta/canonical/OG/JSON-LD land in the PRERENDERED HTML (not just after
 * hydration, the way an imperative useEffect would). Later/nested <Head> usages
 * override earlier duplicates.
 */
export function Seo({ title, description, path, ogImage, noIndex, jsonLd }: SeoMeta) {
  const url = `${SITE_URL}${path}`;
  const image = ogImage ?? `${SITE_URL}/og-image.png`;
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta name="robots" content={noIndex ? "noindex" : "index, follow"} />
      </Head>
      {/* JSON-LD is rendered in the body, not via <Head> — react-head drops
          <script> children, and structured data is valid anywhere in the page,
          so this way it lands in the prerendered HTML for crawlers. */}
      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdSafe(jsonLd) }}
        />
      ) : null}
    </>
  );
}
